import React from 'react';
import { Users, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { formatDate, formatPhone, formatSalary, decodeAddress } from '../utils/formatters';
import { LIST_FILTER_NONE } from '../utils/employeeListFilters';

const FILTER_SELECT_CLASS =
  'h-[38px] w-full min-w-0 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 text-sm';

export const EmployeeTable = ({
  employees,
  employeesToShow,
  totalItems,
  totalPages,
  page,
  setPage,
  rangeStart,
  rangeEnd,
  searchQuery,
  setSearchQuery,
  selectedEmployeeForRole,
  onEmployeeClick,
  listFilters = {},
  filterOptions = { teams: [], supervisors: [], departments: [] },
  onListFilterChange
}) => {
  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0 min-h-[420px]">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Users className="w-5 h-5 text-gray-400 shrink-0" />
            <h2 className="text-xl font-semibold text-white">
              Listado de Empleados
            </h2>
            <Badge className="bg-blue-900/50 text-blue-200">
              {totalItems} {totalItems === 1 ? 'empleado' : 'empleados'}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <label className="flex flex-col gap-1 text-xs text-gray-400 min-w-0">
            Equipo
            <select
              value={listFilters.team || ''}
              onChange={(event) => onListFilterChange?.('team', event.target.value)}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">Todos</option>
              <option value={LIST_FILTER_NONE}>Sin equipo</option>
              {filterOptions.teams.map((team) => (
                <option key={team.value} value={team.value}>
                  {team.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-400 min-w-0">
            Supervisor
            <select
              value={listFilters.supervisor || ''}
              onChange={(event) => onListFilterChange?.('supervisor', event.target.value)}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">Todos</option>
              <option value={LIST_FILTER_NONE}>Sin supervisor</option>
              {filterOptions.supervisors.map((supervisor) => (
                <option key={supervisor.value} value={supervisor.value}>
                  {supervisor.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-400 min-w-0">
            Departamento
            <select
              value={listFilters.department || ''}
              onChange={(event) => onListFilterChange?.('department', event.target.value)}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">Todos</option>
              {filterOptions.departments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
          </label>
            <div className="flex items-center gap-2 px-3 bg-gray-700 rounded-lg border border-gray-600 w-full min-w-0 h-[38px]">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Buscar empleados..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 outline-0 text-white placeholder-gray-400 text-sm w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 w-full min-w-0">
        {employees.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No hay empleados registrados.
            </p>
          </div>
        ) : employeesToShow.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">
              Ningún empleado coincide con la búsqueda o los filtros.
            </p>
          </div>
        ) : (
          <>
            <div className="w-full max-h-[calc(100vh-16rem)] overflow-auto -mx-4 sm:-mx-6">
              <div className="inline-block min-w-full align-middle w-full">
                <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                  <table className="min-w-full w-full divide-y divide-gray-700">
                    <thead className="sticky top-0 z-10 bg-gray-800 shadow-sm [&>tr>th]:bg-gray-800">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Nombre Completo
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Teléfono
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Fecha Contratación
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Base Fábrica
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Tipo Contrato
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Salario
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Título
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Perfil Trabajo
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Centro Costos
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Dirección
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Última Actualización
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-800 divide-y divide-gray-700">
                      {employeesToShow.map((employee, index) => (
                        <tr
                          key={employee.id}
                          onClick={() => onEmployeeClick(employee)}
                          className={`transition-colors ${
                            index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                          } hover:bg-gray-700 cursor-pointer ${
                            selectedEmployeeForRole?.id === employee.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-white">
                                {employee.first_name && employee.last_name
                                  ? `${employee.first_name} ${employee.last_name}`
                                  : employee.first_name || employee.last_name || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <a
                              href={`mailto:${employee.email}`}
                              onClick={(event) => event.stopPropagation()}
                              className="text-sm text-blue-400 hover:text-blue-300 hover:underline"
                            >
                              {employee.email || 'N/A'}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {formatPhone(employee.phone)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {formatDate(employee.hire_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className="bg-violet-900/50 text-violet-200">
                              {employee.factory_base || 'N/A'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {employee.contract_type || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {formatSalary(employee.salary)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            <div className="max-w-xs truncate" title={employee.BusinessTitle || 'N/A'}>
                              {employee.BusinessTitle || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {employee.JobProfileID || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            <div className="max-w-xs truncate" title={employee.CostCenter || 'N/A'}>
                              {employee.CostCenter || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-300">
                            <div className="max-w-xs">
                              <div className="whitespace-pre-line line-clamp-2 text-xs">
                                {decodeAddress(employee.PathAddressData)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1.5">
                              <Badge
                                className={
                                  employee.active
                                    ? 'bg-emerald-900/50 text-emerald-200'
                                    : 'bg-red-900/50 text-red-200'
                                }
                              >
                                {employee.active ? 'Activo' : 'Inactivo'}
                              </Badge>
                              {employee.external && (
                                <Badge className="bg-blue-900/50 text-blue-200">
                                  Externo
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            {formatDate(employee.updated_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {totalItems > 0 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-gray-500">
                  Mostrando {rangeStart}–{rangeEnd} de {totalItems} empleados
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </button>
                  <span className="text-sm text-gray-500 px-2">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
